import { useEffect, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { Play, Loader2, Sparkles } from "lucide-react";

export default function CourseIntroVideo({ courseId, color = "#CCFF00" }) {
  const [status, setStatus] = useState("loading"); // loading|none|generating|ready|failed
  const [url, setUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const vidRef = useRef(null);
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const { data } = await api.get(`/courses/${courseId}/intro-video`);
      setStatus(data.status || "none");
      if (data.url) setUrl(`${API}${data.url}`);
      return data.status;
    } catch { return "none"; }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const s = await fetchStatus();
      if (s === "generating" && !cancelled) {
        pollRef.current = setInterval(async () => {
          const ns = await fetchStatus();
          if (ns === "ready" || ns === "failed") {
            clearInterval(pollRef.current); pollRef.current = null;
          }
        }, 15000);
      }
    })();
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  if (status === "loading" || status === "none" || status === "failed") return null;

  return (
    <div
      data-testid="course-intro-video"
      className="mt-8 relative overflow-hidden border-2"
      style={{ borderColor: color, boxShadow: `6px 6px 0 ${color}` }}
    >
      {status === "generating" && (
        <div className="aspect-video bg-[#0A0A0A] flex flex-col items-center justify-center gap-3 relative">
          <div className="absolute inset-0 grid-bg opacity-40"/>
          <div
            className="absolute inset-0 opacity-30"
            style={{ backgroundImage: `radial-gradient(circle at 30% 40%, ${color}22, transparent 50%), radial-gradient(circle at 70% 60%, ${color}11, transparent 60%)` }}
          />
          <Loader2 className="animate-spin relative" size={40} style={{ color }}/>
          <div className="font-bebas text-3xl tracking-tighter relative">GERANDO A CAPA EM VÍDEO</div>
          <div className="font-manrope text-xs uppercase tracking-[0.3em] text-white/50 flex items-center gap-2 relative">
            <Sparkles size={12}/> IA SORA 2 · leva 2-5 min · fica pronto pra sempre
          </div>
        </div>
      )}
      {status === "ready" && url && (
        <div className="relative">
          <video
            ref={vidRef}
            data-testid="intro-video-el"
            src={url}
            className="w-full aspect-video block bg-black"
            controls
            playsInline
            preload="metadata"
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
          />
          {!playing && (
            <button
              data-testid="intro-video-play"
              onClick={() => vidRef.current?.play()}
              className="absolute inset-0 flex items-center justify-center group"
              aria-label="Tocar capa"
            >
              <div
                className="w-20 h-20 border-2 flex items-center justify-center bg-black/40 backdrop-blur-sm group-hover:scale-110 transition-transform"
                style={{ borderColor: color, color }}
              >
                <Play size={32} className="ml-1"/>
              </div>
            </button>
          )}
          <div className="absolute top-3 left-3 flex items-center gap-2 pointer-events-none">
            <span
              className="font-manrope text-[10px] uppercase tracking-[0.3em] px-2 py-1 border-2 bg-black/60 backdrop-blur-sm"
              style={{ color, borderColor: color }}
            >
              CAPA · IA
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
